import React, { useEffect } from "react";

import {
  Period,
  TimePickerType,
  TimePickerTypeEnum,
  getArrowByType,
  getDateByType,
  setDateByType,
} from "./TimePickerUtils";

import { Input } from "@/components/ui/input";
import { cn } from "@/utils/core/generalUtils";

export interface TimePickerInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  picker: TimePickerType;
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  period?: Period;
  onRightFocus?: () => void;
  onLeftFocus?: () => void;
  limit?: Date | undefined;
  disabled?: boolean;
}

const TimePickerInput = React.forwardRef<
  HTMLInputElement,
  TimePickerInputProps
>(
  (
    {
      className,
      type = "tel",
      value,
      id,
      name,
      date,
      setDate,
      onChange,
      onKeyDown,
      picker,
      period,
      onLeftFocus,
      onRightFocus,
      limit,
      ...props
    },
    ref,
  ) => {
    const [flag, setFlag] = React.useState<boolean>(false);
    const [prevIntKey, setPrevIntKey] = React.useState<string>("0");

    /**
     * allow the user to enter the second digit within 2 seconds
     * otherwise start again with entering first digit
     */
    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (flag) {
        timer = setTimeout(() => {
          setFlag(false);
        }, 2000);
      }

      return (): void => clearTimeout(timer);
    }, [flag]);

    const calculatedValue = React.useMemo(() => {
      if (!date) {
        return "--";
      }

      return getDateByType(date, picker);
    }, [date, picker]);

    const calculateNewValue = (key: string): string => {
      /*
       * If picker is '12hours' and the first digit is 0, then the second digit is automatically set to 1.
       * The second entered digit will break the condition and the value will be set to 10-12.
       */
      if (picker === TimePickerTypeEnum.TWELVE_HOURS) {
        if (flag && calculatedValue.slice(1, 2) === "1" && prevIntKey === "0") {
          return "0" + key;
        }
      }

      return !flag ? "0" + key : calculatedValue.slice(1, 2) + key;
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Tab") {
        return;
      }
      e.preventDefault();
      if (e.key === "ArrowRight") {
        onRightFocus?.();
      }
      if (e.key === "ArrowLeft") {
        onLeftFocus?.();
      }
      if (["ArrowUp", "ArrowDown"].includes(e.key)) {
        if (!date) {
          // If no date, create a new one with current time
          const newDate = new Date(new Date().setHours(0, 0, 0, 0));
          setDate(newDate);

          return;
        }
        const step = e.key === "ArrowUp" ? 1 : -1;
        const newValue = getArrowByType(calculatedValue, step, picker);
        if (flag) {
          setFlag(false);
        }
        const tempDate = new Date(date);
        setDate(
          setDateByType({
            date: tempDate,
            value: newValue,
            type: picker,
            period,
            limit,
          }),
        );
      }
      if (e.key >= "0" && e.key <= "9") {
        if (!date && e.key) {
          if (picker === TimePickerTypeEnum.TWELVE_HOURS) {
            setPrevIntKey(e.key);
            const newDate = new Date().setHours(parseInt(e.key), 0, 0);
            const value = calculateNewValue(e.key);
            const newValue = setDateByType({
              date: new Date(newDate),
              value,
              type: picker,
              period,
              limit,
            });
            setDate(newValue);
            setFlag((prev) => !prev);
          }

          return;
        }

        if (!date) {
          // If no date, create a new one with current time
          const newDate = new Date(new Date().setHours(0, 0, 0, 0));
          setDate(newDate);

          return;
        }
        if (picker === TimePickerTypeEnum.TWELVE_HOURS) {
          setPrevIntKey(e.key);
        }

        const newValue = calculateNewValue(e.key);
        if (flag) {
          onRightFocus?.();
        }
        setFlag((prev) => !prev);
        const tempDate = new Date(date);
        setDate(
          setDateByType({
            date: tempDate,
            value: newValue,
            type: picker,
            period,
            limit,
          }),
        );
      }
    };

    return (
      <Input
        ref={ref}
        id={id || picker}
        name={name || picker}
        className={cn(
          "w-[40px] text-center font-mono tabular-nums caret-transparent focus:bg-accent focus:text-accent-foreground [&::-webkit-inner-spin-button]:appearance-none",
          className,
        )}
        value={value || calculatedValue}
        onChange={(e) => {
          e.preventDefault();
          onChange?.(e);
        }}
        type={type}
        inputMode='decimal'
        onKeyDown={(e) => {
          onKeyDown?.(e);
          handleKeyDown(e);
        }}
        disabled={props.disabled}
        placeholder={!date ? "--" : undefined}
        {...props}
      />
    );
  },
);

TimePickerInput.displayName = "TimePickerInput";

export { TimePickerInput };
